// ============================================================================
// A/B testing — source de vérité CODE (miroir 1:1 de PostHog).
//
//   Chaque test A/B = 1 feature flag PostHog *multivarié* (Experiment).
//   Ce fichier centralise TOUTE la config pour créer/suivre le test :
//     • EXPERIMENTS      → descripteur (clé flag, variantes, métriques suivies)
//     • *_VARIANTS       → le contenu affiché par variante (à éditer à la main)
//     • FEATURE_FLAGS / DEFAULT_FLAG_VALUES → dérivés, ne pas éditer.
//
//   👉 Ajouter/modifier un test = éditer EXPERIMENTS + sa table de variantes,
//      puis répliquer la MÊME clé + les MÊMES variantes côté PostHog.
//   Doc lisible (hypothèse, décision) : ../AB-TESTS.md
// ============================================================================

// --- Descripteur d'expérience (mappe la config PostHog) --------------------
export type ExperimentConfig = {
  key: string; // clé du feature flag PostHog (identique code ↔ PostHog)
  description: string;
  status: 'draft' | 'running' | 'stopped';
  control: string; // clé de la variante de référence
  variantKeys: readonly string[]; // toutes les variantes (control inclus)
  primaryMetric: string; // event de conversion primaire (cf. TRACKING_EVENTS)
  secondaryMetrics: readonly string[]; // events secondaires (par étape d'entonnoir)
};

export const EXPERIMENTS = {
  HOME_COPY: {
    key: 'home-copy',
    description:
      "Angle complet de la copie Home (bandeau + headline + sous-titre + body).",
    status: 'running',
    control: 'control',
    variantKeys: ['control', 'obsolescence', 'chat_agent'],
    primaryMetric: 'lead_qualified',
    secondaryMetrics: [
      'cta_click',
      'form_opened',
      'form_partial_submission',
      'form_submission',
    ],
  },
} as const satisfies Record<string, ExperimentConfig>;

// --- Dérivés (ne pas éditer à la main) -------------------------------------
// Raccourci clés de flags, conservé pour la rétro-compat des imports.
export const FEATURE_FLAGS = {
  HOME_COPY: EXPERIMENTS.HOME_COPY.key,
} as const;

// Valeur par défaut si PostHog est bloqué (AdBlock) ou indisponible → control.
export const DEFAULT_FLAG_VALUES: Record<string, string> = {
  [EXPERIMENTS.HOME_COPY.key]: EXPERIMENTS.HOME_COPY.control,
};

// ============================================================================
// TEST `home-copy` — Variantes de la COPIE HOME
//   On teste l'ANGLE complet : bandeau + headline + sous-titre + paragraphe.
//   Le CTA, le badge vidéo et la copie du gate restent CONSTANTS (cf.
//   Landing.tsx / QualificationForm.tsx) pour isoler la variable testée.
//   Angles issus de ../marketing/angles.md (3 big ideas) + persona.md.
//
//   ⚠️ Les clés ci-dessous DOIVENT correspondre à EXPERIMENTS.HOME_COPY.variantKeys.
// ============================================================================

export type HomeVariant = {
  banner: string; // bandeau d'accroche (haut de page)
  headline: string;
  subheadline: string;
  body: string[]; // paragraphes sous la vidéo
};

export const HOME_VARIANTS: Record<string, HomeVariant> = {
  // V1 — Work-life / journée récupérée (le « soleil », positif). Référence.
  control: {
    banner: "Et si votre agenda vous appartenait à nouveau ?",
    headline:
      "Récupérez une journée de travail par semaine. Sans changer d’outils, sans coder.",
    subheadline:
      "Vous êtes sous pression et vous finissez vos vrais dossiers le soir. Des managers en ETI et grands groupes ont récupéré 7 à 10 h par semaine en 8 semaines — dans Outlook, Teams, Excel. On vous montre exactement comment.",
    body: [
      "CR de réunion en 2 minutes. Reporting qui se met à jour tout seul. Relances qui partent en 10 secondes. Pas en bâclant — mieux, et sans y passer vos soirées. La méthode complète est dans la vidéo.",
    ],
  },
  // V2 — Remplacement / effet multiplicateur (tension de statut).
  obsolescence: {
    banner:
      "Un manager qui maîtrise l’IA fait le travail de cinq. Dans quel camp serez-vous ?",
    headline:
      "Un manager augmenté remplace cinq managers qui n’ont pas l’IA.",
    subheadline:
      "Ce n’est plus une prédiction. Pendant que les directions se réorganisent, ceux qui pilotent l’IA absorbent la charge de toute une équipe — dans leurs outils, sans coder. La méthode qu’ils appliquent, en 8 semaines. Il est encore temps d’être de ce côté-là.",
    body: [
      "Vous utilisez déjà l’IA, mais vous plafonnez : vous faites encore 80 % du boulot à la main. Ceux qui ont franchi le cap délèguent CR, reportings et relances à des agents IA — et abattent en une journée ce qui en prenait cinq. La vidéo vous montre exactement comment.",
    ],
  },
  // V3 — Les outils d'agents IA (namedropping, hyper-persona).
  //   ⚠️ Ajuster les marques citées à ce qui est réellement enseigné.
  chat_agent: {
    banner:
      "ChatGPT, Copilot, Claude… vous les utilisez déjà. Mais en simples chats ou en agents ?",
    headline:
      "Copilot, Claude, Cowork : transformez vos IA en agents qui font le travail à votre place.",
    subheadline:
      "Vous vous êtes arrêté au chat — poser une question, copier la réponse. La vraie bascule, c’est l’agent : une IA qui lit vos mails, ouvre vos Excel et génère vos livrables seule. Avec les outils que vous avez déjà — Microsoft 365, Copilot, Claude. Sans coder.",
    body: [
      "Un chat, vous le pilotez à chaque étape. Un agent — via Copilot, Claude ou Cowork — vous lui déléguez un objectif : « fais mon CR », et il le sort dans Word, structuré, avec décisions et responsables. C’est ce que 1 % des managers ont compris ces 3 derniers mois. La vidéo vous montre comment les configurer.",
    ],
  },
};
